import { Alert, CircularProgress, Grid, Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { Title } from "@src/appV2/redesign/components/Title";
import { useIsRedesignEnabled } from "@src/appV2/redesign/hooks/useIsRedesignEnabled";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { groupBy } from "lodash";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { APP_V2_USER_EVENTS, useLogEffect } from "../lib/analytics";
import { useSupportArticles } from "./api/useSupportArticles";
import { ExternalHelpCenterLinkButton } from "./ExternalHelpCenterLinkButton";
import { SupportArticleGroup } from "./SupportArticleGroup";
import { SupportContactInfo } from "./SupportContactInfo";

export function HelpCenterPage() {
  const { data, isLoading, isError, isSuccess } = useSupportArticles();
  const history = useHistory();
  const isRedesignEnabled = useIsRedesignEnabled();

  const groupedArticles = groupBy(data?.data ?? [], (item) => item.attributes.category);

  useLogEffect(APP_V2_USER_EVENTS.HELP_CENTER_PAGE_VIEWED);

  const handleBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
      return;
    }

    history.push(getLocation(isRedesignEnabled ? "schedule" : "facilityDashboard"));
  }, [history, isRedesignEnabled]);

  return (
    <Stack
      p={5}
      spacing={5}
      height="100%"
      sx={{
        overflowY: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Stack alignItems="center" direction="row" gap={3}>
        <IconButton
          aria-label="Go back"
          iconType="arrow-left"
          size="small"
          variant="outlined"
          onClick={handleBack}
        />
        <Title bold component="h5" variant="h5">
          Help Center
        </Title>
      </Stack>
      <SupportContactInfo />
      <ExternalHelpCenterLinkButton />
      {isLoading && <CircularProgress />}
      {isError && <Alert severity="error">Error loading support articles</Alert>}
      {isSuccess && data.data.length === 0 && <Text>No support articles</Text>}
      {isSuccess && data.data.length > 0 && (
        <Grid container rowGap={6} columns={{ md: 2, xl: 3 }} p={3}>
          {Object.keys(groupedArticles).map((group) => (
            <Grid key={group} item xs={1}>
              <SupportArticleGroup
                key={group}
                title={group}
                supportArticles={groupedArticles[group]}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Stack>
  );
}
